// =============================================================================
// UptMappingPicker — searchable async UPT combobox for provider mappings
// =============================================================================
// Replaces the first-100 <Select> which truncated catalogs and could not
// search by name. Debounced GET /admin/catalog/templates?status=ACTIVE&search=
// (limit 20). Already-mapped UPTs are filtered client-side from the page.
// =============================================================================

import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { api } from '@/lib/api';
import { useDebounce } from '@/hooks/use-debounce';

interface UptOption {
  id: string;
  name: string;
  productType: string;
  brand: string | null;
  modelName: string | null;
}

export interface UptMappingPickerProps {
  value: string;
  onChange: (uptId: string) => void;
  excludeIds: ReadonlySet<string>;
  enabled: boolean;
}

export function UptMappingPicker({
  value,
  onChange,
  excludeIds,
  enabled,
}: UptMappingPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 300);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const optionsQuery = useQuery({
    queryKey: ['admin', 'catalog', 'templates', 'mapping-picker', debouncedQuery],
    queryFn: () => {
      const params = new URLSearchParams({ status: 'ACTIVE', limit: '20' });
      if (debouncedQuery.trim()) params.set('search', debouncedQuery.trim());
      return api.get<{ data: UptOption[] }>(
        `/api/v1/admin/catalog/templates?${params.toString()}`,
      );
    },
    staleTime: 30_000,
    enabled: enabled && open,
  });

  const selectedQuery = useQuery({
    queryKey: ['admin', 'catalog', 'templates', 'mapping-picker-selected', value],
    queryFn: () => api.get<{ data: UptOption }>(`/api/v1/admin/catalog/templates/${value}`),
    staleTime: 60_000,
    enabled: Boolean(value),
  });

  const options = useMemo(
    () => (optionsQuery.data?.data ?? []).filter((u) => !excludeIds.has(u.id)),
    [optionsQuery.data, excludeIds],
  );
  const selected = value
    ? (selectedQuery.data?.data ?? options.find((o) => o.id === value) ?? null)
    : null;

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const selectOption = (id: string) => {
    onChange(id);
    setQuery('');
    setOpen(false);
  };

  const optionLabel = (u: UptOption) => {
    const extra = [u.brand, u.modelName].filter(Boolean).join(' / ');
    return extra ? `${u.name} · ${u.productType} · ${extra}` : `${u.name} · ${u.productType}`;
  };

  return (
    <div className="w-full" ref={containerRef}>
      <label className="mb-1.5 block text-sm font-medium text-text-primary">
        Universal Product Template
      </label>
      <div className="relative">
        {value ? (
          <div className="flex w-full items-center justify-between gap-2 rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary">
            <span className="min-w-0 truncate">
              {selected ? optionLabel(selected) : '…'}
            </span>
            <button
              type="button"
              onClick={() => {
                onChange('');
                setOpen(true);
                requestAnimationFrame(() => inputRef.current?.focus());
              }}
              aria-label="Clear template selection"
              className="shrink-0 rounded p-0.5 text-text-tertiary transition-colors hover:bg-surface-secondary hover:text-text-primary"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        ) : (
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-expanded={open}
            aria-label="Search templates"
            placeholder="Search templates…"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              if (!open) setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            className={clsx(
              'block w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary',
              'transition-colors duration-150 placeholder:text-text-tertiary',
              'focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30',
            )}
          />
        )}

        {open && !value && (
          <div className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-md border border-border bg-surface shadow-lg">
            {optionsQuery.isLoading ? (
              <p className="px-3 py-2.5 text-sm text-text-tertiary">Loading…</p>
            ) : optionsQuery.isError ? (
              <p className="px-3 py-2.5 text-sm text-danger">Failed to load templates.</p>
            ) : options.length === 0 ? (
              <p className="px-3 py-2.5 text-sm text-text-tertiary">
                {debouncedQuery
                  ? `No unmatched templates found for “${debouncedQuery}”.`
                  : 'No unmatched ACTIVE templates on this page.'}
              </p>
            ) : (
              <ul>
                {options.map((u) => (
                  <li key={u.id}>
                    <button
                      type="button"
                      onClick={() => selectOption(u.id)}
                      className="block w-full truncate px-3 py-2 text-left text-sm text-text-primary transition-colors hover:bg-surface-secondary"
                    >
                      <span className="font-medium">{u.name}</span>
                      <span className="text-text-tertiary"> · {u.productType}</span>
                      {(u.brand || u.modelName) && (
                        <span className="text-text-secondary">
                          {' '}· {[u.brand, u.modelName].filter(Boolean).join(' / ')}
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
